import React, { useEffect, useState } from 'react'
import { useScript } from 'src/hooks/useScript.hook'
import RenderIf from 'src/components/common/RenderIf'
import { _MapProps } from 'react-kakao-maps-sdk/dist/components/Map'
import CouponMapPageView from 'src/pages/CouponMapPage/view'
import { useToast } from 'src/provider/ToastContextProvider'
import {
  brandListQueryOption,
  popularBrandQueryOption,
  possessionBrandCouponQueryOption,
  ownedCouponFullListQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  BrandListSpace,
  KakaoMapInfoAuthStatusSpace,
  PopupBrandListSpace,
  PurchaseHistoryListSpace,
  WalletCouponSpace,
} from 'src/types/api'
import { areArraysEqual, filterDistance, getDistanceFromLatLon } from 'src/utils'
import { AnimationControls, useAnimationControls } from 'framer-motion'
import usePageVisibility from 'src/hooks/usePageVisibility.hook'
import { useBlocker } from 'react-router-dom'
import { handleCouponMapBottomSheetAnimation } from 'src/styles/animation'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import useCurrentLocation from 'src/hooks/useCurrentLocation.hook'
import { useModal } from 'src/provider/ModalContextProvider'
import KakaoLocationInfoCorrectTerms from 'src/components/common/Modal/children/KakaoLocationInfoCorrectTerms'
import {
  kakaomapInfoAuthStatusQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import useMapLocalStorage from 'src/hooks/useMapLocalStorage.hook'

export type positionType = { lat: number; lng: number }
export type searchBrandListType = { brandNumber: number; brandName: string; categoryNumber: number }

export type storeMarkerType = Omit<
  kakao.maps.services.PlacesSearchResultItem,
  'x' | 'y' | 'id' | 'category_group_code'
> & {
  position: positionType
  search_keyword: string
  search_map_center?: kakao.maps.LatLng
  brandNumber: number
  categoryNumber: number
}
type Place = kakao.maps.services.PlacesSearchResultItem

export interface CouponMapProps {
  getCurrentLocationInfo: () => void
  handleSearchStores: (searchItem: searchBrandListType) => void
  handleUpdateActiveStoreCard: {
    set: (marker: storeMarkerType) => void
    reset: () => void
  }
  handleSearchBrandListSetting: {
    set: (brand: searchBrandListType) => void
    reset: () => void
  }
  handleSearchStoresByBrandList: () => void
  handleBottomSheetAnimation: {
    initial: { y: string; x: string; opacity: number; scale: number; display: string }
    open: () => void
    close: () => void
  }
  map: kakao.maps.Map | null
  defaultCenter: positionType
  kakaoMapOptions: _MapProps
  storeMarkers?: storeMarkerType[]
  userLocation: positionType | null
  selectedStore?: storeMarkerType | null
  brandList?: BrandListSpace.BrandInfoType[]
  searchBrandList?: searchBrandListType[] | null
  bottomSheetControl: AnimationControls
  bottomSheetToggle: boolean
  ownedCouponCntByBrand?: number
  ownedCouponList?: searchBrandListType[]
  popularBrandList?: searchBrandListType[]
  userInterestBrandList?: searchBrandListType[]
  searchCurrentLocationStore: boolean
  handleUpdateUserInterestBrandList: (event: MouseEvent, brandName: string) => void
  handleBottomSheetOpen: () => void
  handleBottomSheetClose: () => void
  handleSearchBrandListFilter: (brand: searchBrandListType) => void
  filterBrand: searchBrandListType | null
}

const kakaoMapAppKey = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY
const scriptUrl = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${kakaoMapAppKey}&libraries=services,clusterer,drawing&autoload=false`
//여의도역
const defaultCenter = { lat: 37.521715859, lng: 126.924290018 }

const CouponMapPage = () => {
  const { openToast } = useToast()
  const { openModal } = useModal()
  const { c_tel } = useCustomCookies()
  const scriptStatus = useScript(scriptUrl)
  const bottomSheetControl = useAnimationControls()

  const [map, setMap] = useState<kakao.maps.Map | null>(null)
  const [selectedSearBrandList, setSelectedSearBrandList] = useState<
    searchBrandListType[] | undefined
  >([])
  const [bottomSheetToggle, setBottomSheetToggle] = useState<boolean>(false)
  const [searchCurrentLocationStore, setSearchCurrentLocationStore] = useState<boolean>(false)
  const [filterBrand, setFilterBrand] = useState<searchBrandListType | null>(null)

  const {
    userInterestBrandList,
    searchBrandList,
    pastMapCenter,
    pastMapLevel,
    storeMarkers,
    activeStoreMarkers,
    setSearchBrandList,
    setActiveStoreMarkers,
    setStoreMarkers,
    setUserInterestBrandList,
    setPastMapCenter,
  } = useMapLocalStorage(map)
  //바텀 시트 아코디언 브랜드 리스트
  const { data: brandListQueryData } =
    useCustomQuery<BrandListSpace.Response>(brandListQueryOption())
  //보유 쿠폰(전체)
  const { data: allOwnedCouponsListQueryData, isSuccess: isAllOwnedCouponListSuccess } =
    useCustomQuery<WalletCouponSpace.Response>(ownedCouponFullListQueryOption())

  //인기 있는 브랜드
  const { data: popularBrandListQueryData, isSuccess: isPopularBrandListSuccess } =
    useCustomQuery<PopupBrandListSpace.Response>(popularBrandQueryOption())
  //스토어 카드 보유 쿠폰 갯수
  const { data: ownedCouponsListByBrandQueryData } =
    useCustomQuery<PurchaseHistoryListSpace.Response>(
      possessionBrandCouponQueryOption(activeStoreMarkers?.brandNumber, c_tel),
    )

  //카카오 맵 위치 사용 약관 동의 여부
  const { data: kakaoMapTermsAgree, mutateAsync: kakaomapInfoAuthStatusMutate } = useCustomMutation<
    KakaoMapInfoAuthStatusSpace.Request,
    KakaoMapInfoAuthStatusSpace.Response
  >(kakaomapInfoAuthStatusQueryOption())

  //사용자 위치 관련 커스텀 훅
  const { userLocation, handleRetryCurLocation, sentryMapDelayTracker } = useCurrentLocation(
    !!map && kakaoMapTermsAgree?.Body.IsAgree === 'Y',
  )

  //사용자 현재 위치 요청
  const handleCurrentLocation = () => {
    setPastMapCenter(null)
    handleRetryCurLocation()
  }
  //최근 인기 브랜드 리스트 searchBrandListType 타입으로 데이터 가공
  const popularBrandList = popularBrandListQueryData?.Body?.PopularBrand?.map(
    ({ BrandName, BrandNumber, CategoryNumber }) => ({
      brandName: BrandName,
      brandNumber: BrandNumber,
      categoryNumber: CategoryNumber,
    }),
  ) as searchBrandListType[]

  //보유 쿠폰 브랜드 리스트
  const ownedCouponList = allOwnedCouponsListQueryData?.Body?.CouponList?.filter(
    (item, index, self) =>
      Boolean(item.BrandNumber) &&
      index === self.findIndex((t) => t.BrandNumber === item.BrandNumber),
  ).map(({ BrandName, BrandNumber, CategoryNumber }) => ({
    brandName: BrandName,
    brandNumber: BrandNumber,
    categoryNumber: CategoryNumber,
  }))

  //필터 선택 바텀시트 핸들러
  const handleBottomSheetAnimation = {
    initial: handleCouponMapBottomSheetAnimation(bottomSheetControl).initial,
    open: () => {
      handleCouponMapBottomSheetAnimation(bottomSheetControl).open()
      handleUpdateActiveStoreCard.reset()
      setFilterBrand(null)
    },
    close: () => handleCouponMapBottomSheetAnimation(bottomSheetControl).close(),
  }

  //바텀시트(필터) 열기
  const handleBottomSheetOpen = () => {
    setSelectedSearBrandList(searchBrandList)
    setBottomSheetToggle(true)
  }

  //바텀시트(필터) 닫기
  const handleBottomSheetClose = () => {
    const isEmptyStoreMarker = storeMarkers?.length === 0
    const prevSearchBrandList = Array.from(new Set(storeMarkers?.map((w) => w.search_keyword)))
    // 이전 검색어와 중복을 제거한 리스트
    const filterSearchBrandList = selectedSearBrandList!.filter((item) =>
      prevSearchBrandList?.includes(item.brandName),
    )
    setSearchBrandList(isEmptyStoreMarker ? [] : (filterSearchBrandList as searchBrandListType[]))
    setBottomSheetToggle(false)
  }

  // 스토어 마커 활성화 상태 업데이트
  const handleUpdateActiveStoreCard = {
    set: (store: storeMarkerType | null) => setActiveStoreMarkers(store),
    reset: () => setActiveStoreMarkers(null),
  }

  // 브랜드 검색 리스트 설정
  const handleSearchBrandListSetting = {
    set: (brand: searchBrandListType) => {
      const { brandName } = brand
      const alreadyExistBrand = searchBrandList?.findIndex((w) => w.brandName === brandName) !== -1

      switch (true) {
        case searchBrandList?.length === 0:
          return setSearchBrandList([brand])
        case alreadyExistBrand:
          const filterBrandList = searchBrandList!?.filter((w) => w.brandName !== brandName)
          setSearchBrandList(filterBrandList)
          break
        case searchBrandList?.length === 3:
          openToast({ contents: '3개까지 선택 가능합니다.' })
          break
        default:
          setSearchBrandList([...(searchBrandList || []), brand])
      }
    },
    reset: () => setSearchBrandList([]),
  }

  // 브랜드 매장 검색(적용)
  const handleSearchStoresByBrandList = () => {
    if (!map || searchBrandList?.length === 0) {
      setStoreMarkers([])
    } else {
      let store_markers = storeMarkers

      const mapCenterPosition: positionType = {
        lat: map.getCenter().getLat(),
        lng: map.getCenter().getLng(),
      }

      // 이전 검색 리스트
      const prevSearchBrandList = Array.from(new Set(storeMarkers?.map((w) => w.search_keyword)))
      // 현재 검색 리스트(중복 제거)
      const curSearchBrandList = searchBrandList?.map((w) => w.brandName)
      // 이전 검색 리스트와 현재 검색 리스트가 같음
      const isSamePrevSearchBrandList = areArraysEqual(prevSearchBrandList, curSearchBrandList)

      // 이전 검색 좌표와 새롭게 요청한 검색 좌표 300m 이하인 경우
      const isNearByPrevPosition =
        storeMarkers?.length === 0 ||
        filterDistance(
          getDistanceFromLatLon(storeMarkers?.[0]?.position as positionType, mapCenterPosition),
        ) <= 300

      // 검색 실행 조건
      const searchConditions = [
        // 검색 요청 리스트가 비어 있지 않은 경우
        searchBrandList?.length !== 0,
        // 이전 검색리스트와 동일 할 때 검색 위치가 이전 위치보다 300m보다 큰 경우,
        !(isSamePrevSearchBrandList && isNearByPrevPosition),
      ]?.every((w) => w)

      const handleSearchSubmit = () => {
        if (!searchConditions) {
          handleUpdateActiveStoreCard.set(store_markers?.at(0) as storeMarkerType)
        } else {
          // 이전 검색어와 중복을 제거한 리스트
          const filterSearchBrandList = searchBrandList!.filter(
            (item) => !prevSearchBrandList?.includes(item.brandName),
          )
          const searchBrandListData = isNearByPrevPosition ? filterSearchBrandList : searchBrandList

          // 이전 검색 좌표와 새롭게 요청한 검색 좌표 300m 이상인 경우 마커 목록 전체 삭제
          if (!isNearByPrevPosition) {
            store_markers = []
          }

          if (map?.getLevel() > 7) {
            map?.setLevel(7)
            handleSearchStoresByBrandList()
            return
          }

          // 현재 검색리스트에 존재하지않는 마커들 삭제
          store_markers = store_markers?.filter((w) =>
            curSearchBrandList.includes(w.search_keyword),
          )

          Promise.all(
            searchBrandListData!.map((brand) =>
              handleSearchStores(brand).then((res) => {
                if (!!res) {
                  const concatMarkers = [...store_markers, ...res]
                  store_markers = concatMarkers?.sort(
                    (a, b) => filterDistance(a.distance) - filterDistance(b?.distance),
                  )
                }
                return { search_keyword: brand.brandName, data: res }
              }),
            ),
          ).then((results) => {
            const isEmptyResult = results?.every((r) => r.data === undefined)
            if (!isEmptyResult) {
              const filterResults = results
                ?.filter((w) => w.data !== undefined)
                .flatMap((item) => item.data) as storeMarkerType[]

              const sortFilterResults = filterResults?.sort(
                (a, b) => filterDistance(a.distance) - filterDistance(b?.distance),
              )
              // 제일 가까운 매장 카드 오픈
              handleUpdateActiveStoreCard.set(sortFilterResults?.at(0) as storeMarkerType)
            } else {
              //이전에 검색된 결과는 남고 새롭게 검색된 가맹점이 없을떄 제일 가까운 매장 카드 오픈
              store_markers?.length !== 0
                ? handleUpdateActiveStoreCard.set(store_markers?.at(0) as storeMarkerType)
                : //검색결과가 없는 경우
                  openToast({ contents: `검색된 결과가 없습니다.` })
            }

            setSearchBrandList(
              searchBrandList.filter(({ brandName }) => {
                return !results.some(
                  (result) => result.data === undefined && result.search_keyword === brandName,
                )
              }),
            )
            setStoreMarkers(store_markers)
          })
        }
      }
      handleSearchSubmit()
    }
    setBottomSheetToggle(false)
  }

  // 브랜드로 장소 검색
  const handleSearchStores = async (
    searchItem: searchBrandListType,
  ): Promise<storeMarkerType[] | undefined> => {
    const { brandName, brandNumber, categoryNumber } = searchItem
    if (!map) return
    const location = map?.getCenter()
    const ps = new kakao.maps.services.Places()

    // 검색 함수에 Promise 적용
    const searchPlace = (page: number): Promise<Place[]> => {
      return new Promise((resolve, reject) => {
        const searchPlaceCallback = (
          data: kakao.maps.services.PlacesSearchResult,
          status: kakao.maps.services.Status,
          _pagination: kakao.maps.Pagination,
        ) => {
          switch (status) {
            case kakao.maps.services.Status.OK:
              resolve(data)
              break
            case kakao.maps.services.Status.ZERO_RESULT:
              console.warn('검색결과 없음', searchItem.brandName)
              resolve([]) // 결과가 없을 때 빈 배열 반환
              break
            case kakao.maps.services.Status.ERROR:
              console.warn('검색 중 오류가 발생하였습니다.')
              reject(new Error('검색 중 오류 발생'))
              break
            default:
              reject(new Error('알 수 없는 오류'))
          }
        }

        ps.keywordSearch(brandName, searchPlaceCallback, { location, radius: 2000, page })
      })
    }

    try {
      let allPlaces: Place[] = []

      let page = 1
      while (page <= 3) {
        const data = await searchPlace(page)
        if (page === 1 && data.length === 0) {
          throw new Error('검색된 결과가 없습니다.')
        }
        allPlaces = [...allPlaces, ...data]
        // 한 페이지에 15개 미만의 데이터가 있으면 추가 검색 중단
        if (data.length < 15) {
          break
        }
        page++
      }

      return allPlaces.map((place) => ({
        ...place,
        search_keyword: brandName,
        brandNumber,
        categoryNumber,
        search_map_center: location,
        position: { lat: Number(place.y), lng: Number(place.x) },
        distance: !!userLocation
          ? `${getDistanceFromLatLon({ lat: Number(place.y), lng: Number(place.x) }, userLocation)}`
          : `0`,
      }))
    } catch (error: Error | any) {
      console.error('장소 검색 중 오류:', error)
    }
  }

  // 유저 현재 위치가 확인 되면 맵 panTo 및 relayout
  const handlePanToUserLocation = () => {
    if (!!map && !pastMapCenter && !!userLocation) {
      sentryMapDelayTracker.step('map_relayout')
      map?.relayout()
      sentryMapDelayTracker.step('map_panTo')
      map?.panTo(new kakao.maps.LatLng(userLocation.lat, userLocation.lng))

      sentryMapDelayTracker.finish('complete')
    } else {
      sentryMapDelayTracker.step('[skip] map_pan', {
        hasMap: !!map,
        hasPastMapCenter: !!pastMapCenter,
        hasUserLocation: !!userLocation,
      })
      sentryMapDelayTracker.clear()
    }
  }

  // 선택된 브랜드 필터 중 브랜드 별 개별보기 기능
  const handleSearchBrandListFilter = (brand: searchBrandListType) => {
    if (bottomSheetToggle) return
    const isSameBrand = filterBrand?.brandNumber === brand.brandNumber

    if (isSameBrand) {
      setFilterBrand(null)
      handleUpdateActiveStoreCard.set(storeMarkers?.at(0) as storeMarkerType)
    } else {
      setFilterBrand(brand)
      const firstFilterData = storeMarkers
        ?.filter((w) => w.brandNumber === brand.brandNumber)
        ?.at(0)
      handleUpdateActiveStoreCard.set(firstFilterData as storeMarkerType)
    }
    map?.setLevel(6)
  }

  // 선택된 브랜드가 없는 경우 선택된 브랜드 필터 자동 선택
  const handleAutoSetSearchBrand = () => {
    if (
      isAllOwnedCouponListSuccess &&
      isPopularBrandListSuccess &&
      searchBrandList?.length === 0 &&
      !pastMapCenter
    ) {
      if (ownedCouponList?.length !== 0) {
        setSearchBrandList(ownedCouponList!.slice(0, 3))
      } else if (userInterestBrandList?.length !== 0) {
        setSearchBrandList(userInterestBrandList!.slice(0, 3))
      } else if (popularBrandList?.length !== 0) {
        setSearchBrandList(popularBrandList!.slice(0, 3))
      } else return
    }
  }

  //최근에 본 브랜드 칩스 삭제
  const handleUpdateUserInterestBrandList = (event: MouseEvent, brandName: string) => {
    event.stopPropagation()
    const filterList = userInterestBrandList?.filter(
      (item) => item.brandName !== brandName,
    ) as searchBrandListType[]
    setUserInterestBrandList(filterList)
  }

  //현재 위치 요청
  const getCurrentLocationInfo = () => {
    kakaomapInfoAuthStatusMutate({}).then(({ Body: { IsAgree } }) => {
      const children = <KakaoLocationInfoCorrectTerms okCallbackFn={handleCurrentLocation} />

      IsAgree === 'Y' ? handleCurrentLocation() : openModal({ contents: { children } })
    })
  }

  // 카카오맵 현재 위치 사용 권한 팝업
  const handleKakaomapInfoAuthStatus = () => {
    kakaomapInfoAuthStatusMutate({}).then(({ Body: { IsAgree } }) => {
      if (IsAgree === 'N') {
        const btnFn = {
          okCallbackFn: () => {
            handleCurrentLocation()
            handleBottomSheetOpen()
          },
          cancelCallbackFn: handleBottomSheetOpen,
        }
        openModal({ contents: { children: <KakaoLocationInfoCorrectTerms {...btnFn} /> } })
      } else {
        const prevSearchBrandList = Array.from(new Set(storeMarkers?.map((w) => w.search_keyword)))
        // 이전 검색어와 중복을 제거한 리스트
        const filterSearchBrandList = searchBrandList!.filter(
          (item) => !prevSearchBrandList?.includes(item.brandName),
        )
        if (!pastMapCenter || filterSearchBrandList?.length !== 0) {
          handleBottomSheetOpen()
        }
      }
    })
  }

  // 백그라운드=>포어그라인드
  const handleIsForeGround = (isForeground: boolean) => isForeground && map?.relayout()

  // 필터 오픈시 뒤로가기 막기
  useBlocker(bottomSheetToggle)
  usePageVisibility(handleIsForeGround)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleKakaomapInfoAuthStatus, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleAutoSetSearchBrand, [isAllOwnedCouponListSuccess, isPopularBrandListSuccess])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handlePanToUserLocation, [userLocation])

  const kakaoMapOptionFn = {
    onCenterChanged: (m: kakao.maps.Map) => {
      if (m && storeMarkers?.length !== 0) {
        const firstMarkerPosition = storeMarkers?.[0]?.position
        const currentCenterPosition = { lat: m.getCenter()?.getLat(), lng: m.getCenter()?.getLng() }
        const searchCurrentLocationStore =
          filterDistance(getDistanceFromLatLon(firstMarkerPosition, currentCenterPosition)) >= 300
        setSearchCurrentLocationStore((map?.getLevel() as number) < 8 && searchCurrentLocationStore)
      }
    },
    onCreate: (m: kakao.maps.Map) => {
      m?.relayout()
      !map && setMap(m)
    },
  }

  const kakaoMapOptions: _MapProps = {
    isPanto: true,
    level: pastMapLevel ?? 3,
    center: pastMapCenter ?? defaultCenter,
    minLevel: 11,
    maxLevel: 1,
    onIdle: (t) => map?.relayout(),
    ...kakaoMapOptionFn,
  }

  const props: CouponMapProps = {
    getCurrentLocationInfo,
    handleSearchStores,
    handleSearchBrandListSetting,
    handleUpdateActiveStoreCard,
    handleSearchStoresByBrandList,
    map,
    kakaoMapOptions,
    defaultCenter,
    userLocation,
    storeMarkers,
    selectedStore: activeStoreMarkers,
    searchBrandList,
    bottomSheetControl,
    handleBottomSheetAnimation,
    bottomSheetToggle,
    filterBrand,
    popularBrandList,
    ownedCouponList,
    searchCurrentLocationStore,
    brandList: brandListQueryData?.Body?.BrandInfo,
    userInterestBrandList,
    ownedCouponCntByBrand: ownedCouponsListByBrandQueryData?.Body?.CouponCnt,
    handleUpdateUserInterestBrandList,
    handleBottomSheetOpen,
    handleSearchBrandListFilter,
    handleBottomSheetClose,
  }

  return (
    <RenderIf when={scriptStatus === 'ready'}>
      <CouponMapPageView {...props} />
    </RenderIf>
  )
}
export default CouponMapPage
